import { Component, OnInit, OnDestroy } from '@angular/core';
import * as fromCustomersStore from '@appStore/customers';
import * as fromAccountStore from '@appStore/account';
import * as fromConfigurationStore from '@appStore/configuration';
import { Store } from '@ngrx/store';
import { Observable, timer, combineLatest, Subscription } from 'rxjs';
import { filter, switchMap, tap, map, take, combineLatestWith } from 'rxjs/operators';
import { Customer, ApplicationConfiguration } from '@model';
import { SessionStorageConstants } from '@constants';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { SxCookieService } from 'app/services/sx-cookie.service';
import { SxMonitoringService } from 'app/services/sx-monitoring.service';

@Component({
    selector: 'app-landing',
    templateUrl: './landing.component.html',
    standalone: false
})
export class LandingComponent implements OnInit, OnDestroy {
  myCustomers$: Observable<Customer[]>;
  deeplink: string;
  timeOutSubscription: Subscription;
  isIntelligyStartupApplicaton = false;
  private settings: ApplicationConfiguration;

  private subs = new SubSink();

  constructor(
    private accountStore: Store<fromAccountStore.AccountState>,
    private customerStore: Store<fromCustomersStore.CustomersState>,
    private configurationStore: Store<fromConfigurationStore.ConfigurationState>,
    private sxCookieService: SxCookieService,
    private router: Router,
    private monitoringService: SxMonitoringService
  ) {}

  ngOnInit() {
    this.timeOutSubscription = timer(30000).subscribe(_ => this.router.navigate(['/error']));
    
    this.isIntelligyStartupApplicaton = this.getIsIntelligyStartupApplicaton();
    this.deeplink = this.sxCookieService.getCookie('SX_DEEPLINK');
    
    this.subs.sink = this.configurationStore
      .select(fromConfigurationStore.getApiConfiguration)
      .pipe(
        combineLatestWith(this.accountStore.select(fromAccountStore.getIsAuthorized)),
        filter(([appSettings, isAuthorized]) => !!appSettings && isAuthorized),
        map(([appSettings, isAuthorized]) => appSettings)
      ).subscribe(appSettings => {
        this.settings = appSettings;
        
        if (this.isIntelligyStartupApplicaton){
          sessionStorage.setItem(SessionStorageConstants.IntelligyStartIsAuthorized, 'true');
          window.location.href = this.settings.intelligyRedirectUrl;
          return;
        }

        this.customerStore.dispatch(new fromCustomersStore.CustomersLoadData());
        this.subs.sink = this.configurationStore.select(fromConfigurationStore.getPermissionsLoaded)
        .pipe(
          filter(isPermissionsLoaded => !isPermissionsLoaded)
        ).subscribe(_ => {
          this.configurationStore.dispatch(new fromConfigurationStore.LoadPermission());
        });
        
        this.redirect();
      });
  }

  redirect() {
    const customersAndUserInfo$ = combineLatest(
      [ this.customerStore.select(fromCustomersStore.getCustomerState), this.accountStore.select(fromAccountStore.getUserInfoState) ]
    ).pipe(map(results => ({ customerState: results[0], userIfo: results[1] })));

    this.subs.sink = customersAndUserInfo$
      .pipe(
        filter(customersAnduserInfo => customersAnduserInfo.customerState.loaded),
        take(1)
      )
      .subscribe(customersAnduserInfo => {
        this.timeOutSubscription.unsubscribe();

        if (
          !customersAnduserInfo.userIfo.userinfo.onBoardingComplete &&
          !customersAnduserInfo.userIfo.userinfo.impersonatedAsId
        ) {
          this.router.navigate(['/secure/register/verifyemail']);
          return;
        }

        if (!customersAnduserInfo.customerState.data || customersAnduserInfo.customerState.data.length === 0){
          this.router.navigate(['/secure/register']);
          return;
        }

        if (this.router.url.includes('intelligylanding')) {
          this.router.navigate(['/secure/intelligy']);
          return;
        } else if (!this.deeplink) {
          this.router.navigate(['/secure/dashboard']);
        } else {
          this.sxCookieService.clearCookie('SX_DEEPLINK');
          this.router.navigate([this.deeplink]);
        }
      });
  }

  getIsIntelligyStartupApplicaton(): boolean {
    if (sessionStorage.getItem(SessionStorageConstants.IntelligyStart)){
      sessionStorage.removeItem(SessionStorageConstants.IntelligyStart);
      return true;
    }

    return false;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.timeOutSubscription) {
      this.timeOutSubscription.unsubscribe();
    }
  }
}
